import * as Dialog from "@radix-ui/react-dialog";
import * as Sentry from "@sentry/react";
import { useForm, useStore } from "@tanstack/react-form";
import { useSnackbar } from "notistack";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { z } from "zod";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import {
    Button,
    FileInput,
    Input,
    Label,
    SwitchInput,
    TextArea,
    UploadModal,
} from "components/elements";
import { useUpdateDocument } from "hooks/useUpdateDocument";
import { useUploadDocument } from "hooks/useUploadDocument";
import { ClientDocument } from "@compass/shared/types";

// File size limit: in bytes
const MAX_FILE_SIZE = 25 * 1024 * 1024;

export const DocumentUploadSchema = z.object({
    file: z
        .instanceof(File)
        .nullish()
        .refine(
            (file) => {
                if (!file) return true; // Allow null/undefined for edit mode
                return file.size <= MAX_FILE_SIZE;
            },
            {
                message: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
            },
        ),
    filename: z
        .string()
        .max(100, { message: "Display name is too long (maximum 100 characters)." })
        .optional()
        .default(""),
    description: z
        .string()
        .min(0)
        .max(255, { message: "Description is too long (maximum 255 characters)." }),
});

export type DocumentUpload = z.infer<typeof DocumentUploadSchema>;

const fetchFileType = async (file: File): Promise<string> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
            if (reader.result instanceof ArrayBuffer) {
                const uint8Array = new Uint8Array(reader.result);
                const magicNumber = uint8Array.slice(0, 4).join(" ");
                const mimeTypes: Record<string, string> = {
                    "255 216 255 224": "image/jpeg",
                    "255 216 255 225": "image/jpeg",
                    "137 80 78 71": "image/png",
                    "71 73 70 56": "image/gif",
                    "66 77": "image/bmp",
                    "73 73 42 0": "image/tiff",
                    "77 77 0 42": "image/tiff",
                    "37 80 68 70": "application/pdf",
                    "0 0 0 20 102 116 121 112 109 112 52 50": "video/mp4",
                    "73 68 51": "audio/mpeg",
                    "208 207 17 224": "application/vnd.ms-excel",
                    "80 75 3 4": "application/zip",
                };
                resolve(mimeTypes[magicNumber] || "unknown");
            } else {
                resolve("unknown");
            }
        };
        reader.readAsArrayBuffer(file.slice(0, 4));
    });
};

export const UploadDocument = ({
    button,
    title,
    isOpen,
    setIsOpen,
    onUpload,
    blur,
    mode = "create",
    initialValues,
    defaultPublic = false,
    showAIToggle = false,
    notifyContext,
}: {
    button: ReactNode;
    title?: string;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onUpload?: (file: ClientDocument) => void;
    blur?: boolean;
    mode?: "create" | "edit";
    initialValues?: Partial<DocumentUpload & { id?: string }>;
    defaultPublic?: boolean;
    showAIToggle?: boolean;
    /** Where this upload happens, for the admin upload notification (PRTL-1322). */
    notifyContext?: "discussion" | "conversation" | "social" | "documents";
}) => {
    const uploadDocument = useUploadDocument();
    const updateDocument = useUpdateDocument();
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const form = useForm({
        defaultValues: {
            file: null as File | null | undefined,
            filename: "",
            description: "",
            enableAI: false,
            isPublic: defaultPublic,
        },
        onSubmit: async ({ value }) => {
            if (loading) return;
            if (mode === "create" && !value.file) return;

            const parsed = DocumentUploadSchema.parse(value);

            setLoading(true);
            try {
                const sanitized = {
                    ...parsed,
                    ...(parsed.file ? { file: parsed.file } : {}),
                    description: parsed.description || "",
                };

                const sanitizedForUpdate = {
                    ...sanitized,
                    file: sanitized.file === null ? undefined : sanitized.file,
                };

                let file: ClientDocument;
                if (mode === "edit" && initialValues?.id) {
                    file = await updateDocument({ id: initialValues.id, ...sanitizedForUpdate });
                } else {
                    if (!sanitized.file || !(sanitized.file instanceof File)) {
                        throw new Error("Please choose a file to upload.");
                    }
                    file = await uploadDocument({
                        ...sanitized,
                        file: sanitized.file,
                        aiOptOut: !value.enableAI,
                        context: value.isPublic ? "PUBLIC_ATTACHMENT" : "PRIVATE_ATTACHMENT",
                        notifyContext,
                    });
                }

                onUpload?.(file);
                form.reset();
                setSelectedFile(null);
                setIsOpen(false);
            } catch (e) {
                Sentry.captureException(e, { tags: { component: "UploadDocument", mode } });
                // Surface the raw message only when it looks user-facing (short, no stack
                // trace, no "TRPCError", no HTTP status codes). Otherwise fall back to a
                // friendly default so the user doesn't see server-internal detail.
                const raw = e instanceof Error ? e.message : "";
                const looksUserFacing =
                    raw.length > 0 &&
                    raw.length < 200 &&
                    !/TRPCError|fetch|network|status \d|500|403|401/i.test(raw);
                enqueueSnackbar(
                    looksUserFacing
                        ? raw
                        : mode === "edit"
                          ? "We couldn't save your changes. Please try again."
                          : "We couldn't upload your file. Please try again.",
                    { variant: "error" },
                );
            } finally {
                setLoading(false);
            }
        },
    });

    const isDirty = useStore(form.store, (state) => state.isDirty);

    // Load initial values on edit
    useEffect(() => {
        if (isOpen && mode === "edit" && initialValues) {
            form.reset();
            form.setFieldValue("file", null);
            form.setFieldValue("filename", initialValues.filename ?? "");
            form.setFieldValue("description", initialValues.description ?? "");
        }
    }, [isOpen, initialValues, mode]);

    const onUploadInputClear = () => {
        const currentFilename = form.getFieldValue("filename");
        const currentDescription = form.getFieldValue("description");
        form.reset();
        form.setFieldValue("filename", currentFilename);
        form.setFieldValue("description", currentDescription);
        setSelectedFile(null);
    };

    const onUploadInputChange = async (
        e: ChangeEvent<HTMLInputElement>,
        fieldHandleChange: (value: File | null | undefined) => void,
    ) => {
        const { files } = e.target;
        const selectedFile = files?.[0];

        if (!selectedFile) return;

        // Check file size immediately
        if (selectedFile.size > MAX_FILE_SIZE) {
            enqueueSnackbar(`File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`, {
                variant: "error",
            });
            e.target.value = "";
            return;
        }

        let fileType = selectedFile.type;
        let newFileName = selectedFile.name;

        // Any file type is allowed (PRTL-1318). We still try to detect a type when
        // the browser doesn't supply one, purely so the upload carries a content
        // type for preview/icon purposes — detection never blocks the upload.
        if (!fileType) {
            const detected = await fetchFileType(selectedFile);
            if (detected !== "unknown") {
                fileType = detected;
                const fileExtension = fileType.split("/").at(1);
                if (fileExtension && !newFileName.toLowerCase().endsWith(`.${fileExtension}`)) {
                    newFileName += `.${fileExtension}`;
                }
            }
        }

        // Never let an empty content type flow into upload.start — fall back to
        // the generic binary type so presigned upload + previews stay well-formed.
        if (!fileType) {
            fileType = "application/octet-stream";
        }

        const updatedFile = new File([selectedFile], newFileName, {
            type: fileType,
        });

        fieldHandleChange(updatedFile);
        setSelectedFile(updatedFile);

        if (!form.getFieldValue("filename")) {
            form.setFieldValue("filename", selectedFile.name);
        }
    };

    const handleFormSubmit = () => {
        form.handleSubmit();
    };

    return (
        <UploadModal
            button={button}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title={title ?? (mode === "edit" ? "Edit Document" : "Upload Document")}
            blur={blur}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                }}
            >
                <div className="flex flex-col">
                    <div className="py-3 font-semibold text-[#424242]">
                        <span className="text-red-500">*</span> File
                    </div>
                    <form.Field
                        name="file"
                        validators={{
                            onSubmit:
                                mode === "create"
                                    ? z
                                          .instanceof(File)
                                          .nullish()
                                          .refine((file) => file instanceof File, {
                                              message: "File is required.",
                                          })
                                    : z.instanceof(File).nullish(),
                        }}
                    >
                        {(field) => (
                            <>
                                <FileInput
                                    filename={field.state.value?.name}
                                    onChange={(e) =>
                                        onUploadInputChange(e, (file) => field.handleChange(file))
                                    }
                                    handleClear={onUploadInputClear}
                                />
                                {field.state.meta.errors.length > 0 && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {field.state.meta.errors[0]?.message}
                                    </p>
                                )}
                            </>
                        )}
                    </form.Field>

                    <p className="mt-2 text-xs text-gray-500">
                        Max file size: {MAX_FILE_SIZE / (1024 * 1024)}MB.
                    </p>

                    <div className="py-3 font-semibold text-[#424242]">Display name</div>
                    <form.Field
                        name="filename"
                        validators={{
                            onSubmit: z.string().max(100, {
                                message: "Display name is too long (maximum 100 characters).",
                            }),
                        }}
                    >
                        {(field) => (
                            <>
                                <Input
                                    className="bg-[#f7f7f7]"
                                    name={field.name}
                                    value={field.state.value}
                                    placeholder="Optional — defaults to file name"
                                    onBlur={field.handleBlur}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                />
                                {(field.state.meta.errors as unknown as { message: string }[])
                                    .length > 0 && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {
                                            (
                                                field.state.meta.errors as unknown as {
                                                    message: string;
                                                }[]
                                            )[0]?.message
                                        }
                                    </p>
                                )}
                            </>
                        )}
                    </form.Field>

                    <div className="py-3 font-semibold text-[#424242]">Description</div>
                    <form.Field
                        name="description"
                        validators={{ onSubmit: DocumentUploadSchema.shape.description }}
                    >
                        {(field) => (
                            <>
                                <TextArea
                                    className="bg-[#f7f7f7]"
                                    id="description"
                                    name={field.name}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={(e) => field.handleChange(e.target.value)}
                                />
                                {field.state.meta.errors.length > 0 && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {field.state.meta.errors[0]?.message}
                                    </p>
                                )}
                            </>
                        )}
                    </form.Field>

                    {mode === "create" && (
                        <div className="mt-4 flex flex-col gap-3">
                            {showAIToggle && (
                                <form.Field name="enableAI">
                                    {(field) => (
                                        <div>
                                            <SwitchInput
                                                name={field.name}
                                                label="Enable AI enhancements"
                                                value={field.state.value}
                                                onChange={(value) => field.handleChange(value)}
                                            />
                                            <p className="mt-1 text-xs text-gray-500">
                                                {field.state.value
                                                    ? "This file will be sent to a third-party AI service for analysis. Please do not submit files containing sensitive, confidential, or personally identifiable information (PII)."
                                                    : "When enabled, the file will be analyzed by AI to generate a summary and description."}
                                            </p>
                                        </div>
                                    )}
                                </form.Field>
                            )}
                            <form.Field name="isPublic">
                                {(field) => (
                                    <div>
                                        <SwitchInput
                                            name={field.name}
                                            label="Make publicly accessible"
                                            value={field.state.value}
                                            onChange={(value) => field.handleChange(value)}
                                        />
                                        <p className="mt-1 text-xs text-amber-600">
                                            Publicly visible files will always be accessible to
                                            anyone on the internet via a direct URL. Do not upload
                                            sensitive, confidential, or private data as a public
                                            file.
                                        </p>
                                    </div>
                                )}
                            </form.Field>
                        </div>
                    )}

                    <div className="h-4" />
                    <div className="flex flex-row items-end justify-end gap-3">
                        {/* PRTL-1320: do NOT wrap the submit button in <Dialog.Close>.
                            The upload is async — closing the modal synchronously on click
                            unmounts it before `onUpload(file)` has added the new file to the
                            composer's attachment state, so a subsequent "Save changes" omits
                            the just-uploaded attachment. The modal closes via setIsOpen(false)
                            inside onSubmit, only after the upload has fully resolved. */}
                        <Button
                            type="button"
                            disabled={
                                loading ||
                                (mode === "create" ? !selectedFile : !isDirty && !selectedFile)
                            }
                            isLoading={loading}
                            variant="primary"
                            className="text-sm"
                            onClick={handleFormSubmit}
                        >
                            {mode === "edit" ? "Save Changes" : "Upload"}
                            <SubmitIcon className="ml-2" />
                        </Button>
                        <Dialog.Close asChild>
                            <Button
                                variant="secondary"
                                className="bg-[#f2f2f2] text-sm hover:bg-gray-200"
                                disabled={loading}
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </Button>
                        </Dialog.Close>
                    </div>
                </div>
            </form>
        </UploadModal>
    );
};
